/**
 * Match scorer — the deterministic per-attribute confidence engine (Phase Wellspring W2a).
 *
 * Scores a (supplier-row, candidate-listing) attribute pair into a blended 0–100
 * confidence — the value that becomes `products.arbi_confidence_score` — plus a
 * per-attribute breakdown. Three cheap rungs, blended by weight (pipeline-plan §4):
 *
 *   • part# (THE hard signal) — normalized exact-match across a UNION of
 *     {supplier_part_no, mpn, model} on EACH side. 100 = a normalized part# is
 *     shared; 0 = both sides carry part#s but none match; 50 = either side has
 *     none (uncertainty). The union is the CONFLATION GUARD: the part-number
 *     signal lands in different columns per sheet (the old Algopix import parked
 *     it in `model`; the W1 registry maps Altronix item-codes to
 *     supplier_part_no/mpn), so a single-column rung would silently see NO
 *     part-signal on the very sheets we ingest.
 *   • title (soft) — pg_trgm-faithful trigram Jaccard similarity ×100 (mirrors
 *     the production `idx_products_title_trgm`). 100 = identical; 0 = disjoint
 *     OR either side empty.
 *   • brand (light) — case/punctuation-folded equality. 100 = folded-equal;
 *     0 = both present and differ; 50 = either missing.
 *
 * Cheap rungs ONLY — there is deliberately NO image-AI rung here (pipeline-plan
 * §13: the image rung is added only if the MEASURED cheap-rung accuracy falls
 * short — a spike OUTPUT, not built upfront).
 *
 * PURE: no React, no network, no supabase, no I/O (the markup.ts / upcNormalize.ts
 * precedent). The scorer reads attribute values it is handed; the caller maps a
 * mapped sheet row and a Keepa candidate into MatchAttributes.
 */

// ── Tuning surface (SEED values — empirically fit, NOT frozen) ────────────────
//
// The weights and thresholds below are SEED starting points. They are EMPIRICALLY
// FIT by the W2a Phase-3 go/no-go spike against the labeled sample
// (docs/wellspring/w2a-fixtures/labeled-sample.json), per pipeline-plan §4: "the
// exact attribute weights and the confidence threshold ... better decided against
// the labeled sample than on the whiteboard." They are NOT frozen — the spike
// report (docs/wellspring/w2a-go-no-go.md) recommends the final set, and
// `scoreMatch` takes an `opts` override so the spike can sweep alternatives
// WITHOUT editing this module.
//
// Seed rationale (the single-linear-blend tension this surfaces, to be measured):
//  - part# is the highest single weight (it is THE hard signal) but NOT so high
//    that a missing part# (50) caps an otherwise-strong title+brand "easy" row
//    below trust (the Russound false-negative failure mode).
//  - …yet high enough that a definitive part# match (100) clears trust with only
//    modest title help (the Altronix cryptic-row failure mode — the rows that
//    justify building the matcher at all).
//  A single linear blend serves BOTH failure modes only within a threshold band;
//  whether that band exists for THIS catalog is exactly what Phase 3 measures.

/** Per-attribute blend weights. Need not sum to 1 — the blend normalizes by their sum. */
export interface MatchWeights {
  partNo: number;
  title: number;
  brand: number;
}

/** Verdict cut-points on the 0–100 blended score. */
export interface MatchThresholds {
  /** score >= trust   → 'trust'  (auto-accept candidate / surface as confident). */
  trust: number;
  /** score >= suspect → 'suspect' (show, but flag for human review). */
  suspect: number;
}

export const DEFAULT_MATCH_WEIGHTS: MatchWeights = { partNo: 0.5, title: 0.35, brand: 0.15 };
export const DEFAULT_MATCH_THRESHOLDS: MatchThresholds = { trust: 65, suspect: 40 };

/**
 * Minimum normalized part-number length. A 1-char "part#" (a stray model cell of
 * "A") is too weak to be a real identifier and would false-match across rows, so
 * it is treated as no part-signal. Tunable like the weights/thresholds.
 */
export const MIN_PART_NO_LEN = 2;

// ── I/O shapes ────────────────────────────────────────────────────────────────

/**
 * One side of a match comparison — the attributes the cheap rungs read. BOTH the
 * supplier row (from the mapped sheet) and a candidate listing (e.g. a Keepa hit)
 * present this same shape; the caller maps each side's fields in. Any field may be
 * absent (null/undefined/empty), which the rungs handle as "missing".
 */
export interface MatchAttributes {
  /** Part-number-class identifiers. The part# rung scores the UNION of all three
   *  (the conflation guard) — the signal lands in different columns per sheet. */
  supplierPartNo?: string | null;
  mpn?: string | null;
  model?: string | null;
  title?: string | null;
  brand?: string | null;
}

/** One rung's contribution to the breakdown. */
export interface AttributeScore {
  /** 0–100 similarity for this attribute. */
  score: number;
  /** True when the rung had information on BOTH sides. False = the "missing" case
   *  (part#/brand scored a neutral 50; title scored a pg_trgm-faithful 0). This
   *  flag is descriptive metadata for the breakdown/report — the blend uses
   *  `score`, not this flag. */
  informative: boolean;
  /** Short human-readable note (breakdown / debugging; not UI copy). */
  note: string;
}

export interface MatchScoreBreakdown {
  partNo: AttributeScore;
  title: AttributeScore;
  brand: AttributeScore;
}

export type MatchVerdict = 'trust' | 'suspect' | 'hold';

export interface MatchScoreResult {
  /** Blended 0–100 confidence — this IS the `arbi_confidence_score` value. */
  score: number;
  verdict: MatchVerdict;
  breakdown: MatchScoreBreakdown;
  /** The weights actually used (echoed for provenance / spike tuning). */
  weights: MatchWeights;
}

/** Optional per-call overrides so the Phase-3 spike can sweep weights/thresholds. */
export interface ScoreMatchOptions {
  weights?: MatchWeights;
  thresholds?: MatchThresholds;
}

// ── Part# rung ──────────────────────────────────────────────────────────────

/**
 * Normalize a raw part#/model/MPN value to a comparable token: drop an Excel
 * float artifact ("6030.0" → "6030"), uppercase, strip every non-alphanumeric
 * (hyphens/slashes/spaces/dots). Returns null when empty or shorter than
 * MIN_PART_NO_LEN after normalization.
 */
export function normalizePartNo(raw: string | null | undefined): string | null {
  let s = String(raw ?? '').trim();
  if (!s) return null;
  // Excel reads a pure-integer part# as a float ("6030" → "6030.0"); drop the .0…
  if (/^\d+\.0+$/.test(s)) s = s.slice(0, s.indexOf('.'));
  s = s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length >= MIN_PART_NO_LEN ? s : null;
}

/**
 * The UNION of normalized part# tokens across {supplierPartNo, mpn, model} for one
 * side. This is the conflation guard — wherever the part# landed, it is collected.
 */
export function extractPartNoSet(attrs: MatchAttributes): Set<string> {
  const set = new Set<string>();
  for (const raw of [attrs.supplierPartNo, attrs.mpn, attrs.model]) {
    const n = normalizePartNo(raw);
    if (n) set.add(n);
  }
  return set;
}

/** Part# rung: 100 (a normalized token is shared) / 0 (both have part#s, none shared) / 50 (either side has none). */
export function scorePartNo(a: MatchAttributes, b: MatchAttributes): AttributeScore {
  const A = extractPartNoSet(a);
  const B = extractPartNoSet(b);
  if (A.size === 0 || B.size === 0) {
    const side =
      A.size === 0 && B.size === 0
        ? 'both sides'
        : A.size === 0
          ? 'supplier side'
          : 'candidate side';
    return { score: 50, informative: false, note: `part# missing (${side})` };
  }
  for (const t of A) {
    if (B.has(t)) return { score: 100, informative: true, note: `part# exact match (${t})` };
  }
  return {
    score: 0,
    informative: true,
    note: `part# conflict (${[...A].join(',')} vs ${[...B].join(',')})`,
  };
}

// ── Title rung (pg_trgm-faithful) ─────────────────────────────────────────────

/**
 * pg_trgm trigram set: lowercase, split on non-alphanumeric into words, pad each
 * word with two leading + one trailing space, emit every 3-char window (deduped).
 * Mirrors PostgreSQL's pg_trgm show_trgm so TS scoring tracks the production
 * idx_products_title_trgm index semantics.  show_trgm('cat') = {"  c"," ca","cat","at "}.
 */
function trigramSet(input: string | null | undefined): Set<string> {
  const words = String(input ?? '')
    .toLowerCase()
    .match(/[a-z0-9]+/g);
  const set = new Set<string>();
  if (!words) return set;
  for (const w of words) {
    const padded = `  ${w} `; // 2 leading + 1 trailing space (pg_trgm)
    for (let i = 0; i + 3 <= padded.length; i++) set.add(padded.slice(i, i + 3));
  }
  return set;
}

/** Trigram Jaccard similarity in [0,1] — |A∩B| / |A∪B| over the trigram sets (pg_trgm `similarity`). */
export function trigramSimilarity(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const A = trigramSet(a);
  const B = trigramSet(b);
  if (A.size === 0 && B.size === 0) return 0; // pg_trgm: empty/empty → 0
  let inter = 0;
  const [small, large] = A.size <= B.size ? [A, B] : [B, A];
  for (const t of small) if (large.has(t)) inter++;
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}

/** Title rung: trigram similarity ×100. Either side empty → 0 (pg_trgm semantics). */
export function scoreTitle(a: MatchAttributes, b: MatchAttributes): AttributeScore {
  const aT = String(a.title ?? '').trim();
  const bT = String(b.title ?? '').trim();
  if (!aT || !bT)
    return { score: 0, informative: false, note: 'title missing on one/both sides (pg_trgm → 0)' };
  const sim = trigramSimilarity(aT, bT);
  return {
    score: round2(sim * 100),
    informative: true,
    note: `title trigram ${(sim * 100).toFixed(1)}%`,
  };
}

// ── Brand rung ────────────────────────────────────────────────────────────────

/** Fold a brand for equality: lowercase, strip non-alphanumeric. Empty → null. */
export function normalizeBrand(raw: string | null | undefined): string | null {
  const s = String(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  return s.length ? s : null;
}

/** Brand rung: 100 (folded-equal) / 0 (both present, differ) / 50 (either missing). */
export function scoreBrand(a: MatchAttributes, b: MatchAttributes): AttributeScore {
  const A = normalizeBrand(a.brand);
  const B = normalizeBrand(b.brand);
  if (!A || !B) return { score: 50, informative: false, note: 'brand missing on one/both sides' };
  if (A === B) return { score: 100, informative: true, note: `brand match (${A})` };
  return { score: 0, informative: true, note: `brand differs (${A} vs ${B})` };
}

// ── Blend + verdict ───────────────────────────────────────────────────────────

/** Weighted blend of the three rung scores, normalized by the weight sum, clamped to [0,100], 2dp. */
export function blendScores(breakdown: MatchScoreBreakdown, weights: MatchWeights): number {
  const raw =
    breakdown.partNo.score * weights.partNo +
    breakdown.title.score * weights.title +
    breakdown.brand.score * weights.brand;
  const wsum = weights.partNo + weights.title + weights.brand;
  const blended = wsum === 0 ? 0 : raw / wsum;
  return round2(Math.max(0, Math.min(100, blended)));
}

/** Map a 0–100 score to a verdict via the thresholds. */
export function verdictFor(score: number, thresholds: MatchThresholds): MatchVerdict {
  if (score >= thresholds.trust) return 'trust';
  if (score >= thresholds.suspect) return 'suspect';
  return 'hold';
}

/**
 * Score a (supplier-row, candidate-listing) attribute pair → blended 0–100
 * confidence + per-attribute breakdown + verdict. The default weights/thresholds
 * are the seed constants; pass `opts` to sweep alternatives (Phase-3 tuning).
 */
export function scoreMatch(
  a: MatchAttributes,
  b: MatchAttributes,
  opts: ScoreMatchOptions = {}
): MatchScoreResult {
  const weights = opts.weights ?? DEFAULT_MATCH_WEIGHTS;
  const thresholds = opts.thresholds ?? DEFAULT_MATCH_THRESHOLDS;
  const breakdown: MatchScoreBreakdown = {
    partNo: scorePartNo(a, b),
    title: scoreTitle(a, b),
    brand: scoreBrand(a, b),
  };
  const score = blendScores(breakdown, weights);
  return { score, verdict: verdictFor(score, thresholds), breakdown, weights };
}

// ── internal ──────────────────────────────────────────────────────────────────

/** Round to 2 decimals (arbi_confidence_score is numeric(5,2)); float-safe. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
